import { FieldType } from '@ngx-formly/material';
import { addDays, addHours, addMonths, addWeeks, format } from 'date-fns';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { t } from '@lib/translation';

import { Modes } from '../../enums/modes.enum';
import { Styles } from '../../enums/styles.enum';
import { IFsDateRangePickerFormFieldConfig } from '../../interfaces/date-range-picker-form-field-config.interface';
import { IFsDateRangePickerFieldTemplateOptions } from '../../interfaces/date-range-picker-form-field-template-options.interfaces';
import { IRange } from '../../interfaces/range.interface';
import { CustomDateRangeDialogComponent } from '../custom-date-range-dialog/custom-date-range-dialog.component';

@Component({
  selector: 'fs-date-range-picker',
  templateUrl: './date-range-picker.form-field.html',
  styleUrls: ['./date-range-picker.form-field.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FsDateRangePickerFormField
  extends FieldType<IFsDateRangePickerFormFieldConfig>
  implements OnDestroy, OnInit
{
  public defaultOptions: IFsDateRangePickerFormFieldConfig = {
    type: 'date-range-picker',
    templateOptions: {
      startTimestampKey: 'event_timestamp__gte',
      endTimestampKey: 'event_timestamp__lte',
      custom: true,
      appearance: 'none',
      style: Styles.Button,
      ranges: [
        { value: '1', mode: Modes.Day, label: '1D' },
        { value: '1', mode: Modes.Week, label: '1W' },
        { value: '2', mode: Modes.Week, label: '2W' },
        { value: '1', mode: Modes.Month, label: '1M' },
      ],
    },
  };

  public ranges: IRange[] = [];
  public startDate: number;
  public endDate: number;
  public dateSelection;
  public searchRangeMessage: string;
  public customDates: any = {};
  private readonly _destroy$ = new Subject<void>();

  constructor(private readonly _cd: ChangeDetectorRef, private readonly _matDialog: MatDialog) {
    super();
  }

  public ngOnInit(): void {
    this.ranges = this.templateOptions.ranges;
    this._patchForm();
    this._cd.detectChanges();
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public onDateSelectionChange(event: any): void {
    if (event.value === 'custom') {
      const ref = this._matDialog.open(CustomDateRangeDialogComponent, {
        data: this.customDates,
      });

      ref
        .afterClosed()
        .pipe(takeUntil(this._destroy$))
        .subscribe((res) => {
          if (res) {
            this.startDate = Math.floor(res.startDate.getTime() / 1000);
            this.endDate = Math.floor(res.endDate.getTime() / 1000);
            this.customDates = {
              startDate: res.startDate,
              endDate: res.endDate,
            };
            this._setKeys();
            this._cd.detectChanges();
            this._updateRangeMessage(res.startDate, res.endDate);
          } else {
            if (Object.keys(this.customDates).length) {
              this._updateRangeMessage(this.customDates.startDate, this.customDates.endDate);
            }
          }
        });
    } else {
      this.searchRangeMessage = '';
      const start = new Date();
      let startDate;
      switch (event.value.mode) {
        case Modes.Hour:
          startDate = addHours(start, +-event.value.value);
          this.startDate = Math.floor(startDate.getTime() / 1000);
          break;
        case Modes.Day:
          startDate = addDays(start, +-event.value.value);
          this.startDate = Math.floor(startDate.getTime() / 1000);
          break;
        case Modes.Week:
          startDate = addWeeks(start, +-event.value.value);
          this.startDate = Math.floor(startDate.getTime() / 1000);
          break;
        case Modes.Month:
          startDate = addMonths(start, +-event.value.value);
          this.startDate = Math.floor(startDate.getTime() / 1000);
          break;
      }

      const end = new Date();
      this.endDate = Math.floor(end.getTime() / 1000);
      this._setKeys();
    }
  }

  public change(value: any): void {
    this.formControl.setValue(value);
    this.to.change(this.field, value);
  }

  private _setKeys(): void {
    const startKey = this.templateOptions.startTimestampKey;
    const endKey = this.templateOptions.endTimestampKey;

    const value = {};
    value[startKey] = this.startDate;
    value[endKey] = this.endDate;
    this.formControl.setValue(value);
    this.change(value);
  }

  get templateOptions(): IFsDateRangePickerFieldTemplateOptions {
    return this.field.templateOptions;
  }

  private _patchForm(): void {
    if (this.value) {
      this.customDates.startDate = this.value[this.templateOptions.startTimestampKey];
      this.customDates.endDate = this.value[this.templateOptions.endTimestampKey];
      this.templateOptions.custom = true;
      this._updateRangeMessage(this.customDates.startDate, this.customDates.endDate);
    }
    this._cd.detectChanges();
  }

  private _updateRangeMessage(start: number | Date, end: number | Date): void {
    const startFormatted = format(start, 'MMM-d HH:mm:ss');
    const endFormatted = format(end, 'MMM-d HH:mm:ss');
    this.searchRangeMessage = t('map.drawing.dateRangePicker.searchingBetween', { 
      startDate: startFormatted, 
      endDate: endFormatted 
    });
    this._cd.detectChanges();
    this.dateSelection = this.searchRangeMessage;
    this._cd.detectChanges();
  }
}
